'use client';

import { Icons } from '@/components/icons';
import { HiddenOnDynamicEnvs } from '@/components/hidden-on-dynamic-envs';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { cn } from '@/lib/cn';
import { useSearchAccess } from '@/lib/datawarehouse/search/use-search-access';
import { GlobalSearchTrigger } from '@/lib/global-search-v2/switch';
import { useSession } from '@/providers/session-provider';
import { useFetchAllIds } from '@/requests/hooks/use-fetch-all-ids';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import type { OrganizationUser } from '@pigello/pigello-matrix';
import {
  OrganizationUserConfig,
  OrganizationUserWorkEngagementConfig,
  type OrganizationUserWorkEngagement,
} from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { DashboardNotes } from './components/dashboard-notes';
import { DashboardNotifications } from './components/dashboard-notifications';
import { HandleDashboardModal } from './components/handle-dashboard-modal';
import { IntegrationAttentionBanner } from './components/integration-attention-banner';
import { IntegrationUpgradesBanner } from './components/integration-upgrades-banner';
import { KeyIndexes } from './components/key-indexes';
import { ReleaseNoteDashboardBanner } from './components/release-note-banner';
import { SystemEvents } from './components/system-events';
import { ToHandleWidget } from './components/to-handle-widget';
import { WorkflowsWidget } from './components/workflows-widget';
import { useGetDashboards } from './hooks/use-get-dashboard';
import { useGetDashboardWorkEngagement } from './hooks/use-get-dashboard-external-client';
import type { DashboardSettings } from './types';
import { getGreeting } from './utils';

export default function DashboardPage() {
  const {
    data: {
      session: { userId },
      user,
    },
  } = useSession();
  const searchAccess = useSearchAccess();
  const useV2GlobalSearch = useFeatureFlag(FEATURE_FLAGS.GLOBAL_SEARCH_V2);
  const { data: workEngagements, isLoading: isLoadingWorkEngagements } =
    useFetchAllInstances<OrganizationUserWorkEngagement>({
      modelName: OrganizationUserWorkEngagementConfig.modelName,
      filters: {
        user: {
          noop: userId,
        },
      },
      enabled: !!userId,
      nested: ['workEngagement'],
      enableGlobalFilters: true,
    });

  const workEngagementIds = [
    ...new Set(
      (workEngagements?.list ?? [])
        .map((w) => w.workEngagement?.id)
        .filter((id) => id)
    ),
  ] as string[];

  const [dashboardKey, setDashboardKey] = useLocalStorage<string | undefined>(
    'dashboardKey',
    undefined
  );

  const {
    data: dashboards,
    isPending: isLoadingDashboards,
    isRefetching: isLoadingDashboardsRefetching,
  } = useGetDashboards();

  // On first render, prefer the favorite dashboard; fallback to the first available
  const hasSetDefaultDashboard = useRef(false);
  useEffect(() => {
    if (dashboardKey || hasSetDefaultDashboard.current) {
      return;
    }
    const favorite = dashboards?.find((d) => d.ranking === 0);
    const targetKey = favorite?.key ?? dashboards?.[0]?.key;
    if (!targetKey) return;
    setDashboardKey(targetKey);
    hasSetDefaultDashboard.current = true;
  }, [dashboards, dashboardKey, setDashboardKey]);

  const workEngagementIdsCount = workEngagementIds.length;
  const {
    data: workEngagementsDashboards,
    isLoading: isLoadingWorkEngagementsDashboards,
    isFetched: isWorkEngagementsDashboardsFetched,
  } = useGetDashboardWorkEngagement({
    workEngagementIds: workEngagementIds,
    enabled: workEngagementIdsCount > 0,
  });

  const isLoading =
    isLoadingDashboards ||
    isLoadingWorkEngagementsDashboards ||
    isLoadingWorkEngagements;

  const allDashboards = useMemo(
    () => [
      ...(Array.isArray(dashboards) ? dashboards : []),
      ...(Array.isArray(workEngagementsDashboards)
        ? workEngagementsDashboards
        : []),
    ],
    [dashboards, workEngagementsDashboards]
  );

  const currentDashboard = allDashboards?.find(
    (dashboard) => dashboardKey === dashboard.key
  );

  const favoriteDashboard = dashboards?.find(
    (dashboard) => dashboard.ranking === 0
  );
  const isWorkEngagementDashboard =
    !!currentDashboard && 'work_engagement_id' in currentDashboard;

  const createdByOrganizationUserIds = (workEngagementsDashboards ?? [])?.map(
    (we) => we.created_by_user_id
  );

  const [dashboardSettings] = useLocalStorage<DashboardSettings>(
    'dashboardSettings',
    { hiddenItems: {} }
  );

  const hiddenKeyIndexes = dashboardSettings.hiddenItems['key-indexes'] ?? [];

  const { data: createdByOrganizationUsers } = useFetchAllIds<OrganizationUser>(
    {
      modelName: OrganizationUserConfig.modelName,
      ids: createdByOrganizationUserIds,
      idAttr: 'id',
      enabled: !!createdByOrganizationUserIds?.length,
    }
  );

  const firstDashboardKey = dashboards?.[0]?.key;
  const firstWorkEngagementDashboardKey = workEngagementsDashboards?.[0]?.key;

  const isDashboardKeyResolutionReady =
    !isLoadingDashboards &&
    !isLoadingDashboardsRefetching &&
    !isLoadingWorkEngagements &&
    (workEngagementIdsCount === 0 || isWorkEngagementsDashboardsFetched);

  useEffect(() => {
    if (!isDashboardKeyResolutionReady) {
      return;
    }

    const dashboardKeyExists =
      dashboards?.some((d) => d.key === dashboardKey) ||
      workEngagementsDashboards?.some((d) => d.key === dashboardKey);

    if (dashboardKey && dashboardKeyExists) {
      return;
    }

    setDashboardKey(
      favoriteDashboard?.key ??
        firstDashboardKey ??
        firstWorkEngagementDashboardKey
    );
  }, [
    workEngagementsDashboards,
    dashboards,
    dashboardKey,
    favoriteDashboard,
    firstDashboardKey,
    firstWorkEngagementDashboardKey,
    isDashboardKeyResolutionReady,
    setDashboardKey,
  ]);

  const showKeyIndexes =
    !currentDashboard ||
    (currentDashboard && !hiddenKeyIndexes.includes(currentDashboard.key));

  return (
    <div className='relative flex size-full flex-col gap-8 bg-background p-6'>
      <div className='flex w-full flex-row items-center justify-between'>
        <div className='flex flex-col gap-2'>
          <h1>
            {getGreeting()} {user.communicationName} 👋
          </h1>
          <div className='flex items-center gap-2 text-sm font-medium text-muted-foreground'>
            <Icons.dateRange className='size-6' />
            <span>{DateTime.now().toFormat('cccc d LLL')}</span>
          </div>
        </div>
        <HandleDashboardModal
          dashboardKey={dashboardKey}
          setDashboardKey={setDashboardKey}
          dashboards={dashboards}
          workEngagementsDashboards={workEngagementsDashboards}
          isLoadingDashboards={isLoading}
          isWorkEngagementDashboard={isWorkEngagementDashboard}
          organizationUserWorkEngagements={workEngagements?.list}
          createdByOrganizationUsers={createdByOrganizationUsers?.list}
        />
      </div>
      <ReleaseNoteDashboardBanner />
      <HiddenOnDynamicEnvs>
        <IntegrationAttentionBanner />
        <IntegrationUpgradesBanner />
      </HiddenOnDynamicEnvs>
      {useV2GlobalSearch && (
        <Card className='w-full gap-0 p-0'>
          <CardHeader className='gap-2 border-b-0 px-4 pt-4 text-sm font-medium'>
            <div className='flex items-center gap-2'>
              <CardTitle>Sök</CardTitle>
              <Badge variant='yellow'>Beta</Badge>
            </div>
            <CardDescription>{searchAccess.description}</CardDescription>
          </CardHeader>
          <CardContent className='px-4 pb-4'>
            <GlobalSearchTrigger />
          </CardContent>
        </Card>
      )}
      <div
        className={cn(
          'grid grid-cols-8 gap-8 lg:grid-cols-7',
          isWorkEngagementDashboard && 'grid-cols-6 lg:grid-cols-6'
        )}
      >
        <div className='col-span-full'>
          <WorkflowsWidget
            isWorkEngagementDashboard={isWorkEngagementDashboard}
            dashboardWorkEngagementId={
              currentDashboard && 'work_engagement_id' in currentDashboard
                ? currentDashboard.work_engagement_id
                : undefined
            }
          />
        </div>

        {showKeyIndexes && (
          <div className='col-span-full'>
            <KeyIndexes
              dashboards={allDashboards}
              dashboardKey={dashboardKey}
              isWorkEngagementDashboard={isWorkEngagementDashboard}
            />
          </div>
        )}

        <div
          className={cn(
            'col-span-full lg:col-span-5',
            isWorkEngagementDashboard && 'col-span-full lg:col-span-full'
          )}
        >
          <ToHandleWidget
            dashboards={allDashboards}
            dashboardKey={dashboardKey}
            isWorkEngagementDashboard={isWorkEngagementDashboard}
          />
        </div>
        {!isWorkEngagementDashboard && (
          <div className='col-span-full lg:col-span-2'>
            <DashboardNotes
              dashboards={allDashboards}
              dashboardKey={dashboardKey}
              isLoadingDashboards={isLoadingDashboards}
            />
          </div>
        )}
        <div
          className={cn(
            'col-span-full lg:col-span-5',
            isWorkEngagementDashboard && 'col-span-full lg:col-span-4'
          )}
        >
          <SystemEvents
            dashboards={allDashboards}
            dashboardKey={dashboardKey}
            isWorkEngagementDashboard={isWorkEngagementDashboard}
          />
        </div>
        <div
          className={cn(
            'col-span-full lg:col-span-2',
            isWorkEngagementDashboard && 'col-span-full lg:col-span-2'
          )}
        >
          <DashboardNotifications
            dashboards={allDashboards}
            dashboardKey={dashboardKey}
          />
        </div>
      </div>
    </div>
  );
}
